import { useWebgate } from "@xvia/webgate-connect";
import { FC, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getProfileRecommendationsByUsername } from "../../apis/profile-recommendations";
import { Catalog } from "../../apis/search-catalog";
import { IconPerfil } from "./icones-perfis";
import {
  ProfileRecommendations,
  RelevantServicesProps,
} from "./profile-recommendations";
import { perfis } from "./profiles-mapping";

export interface ProfileRecommendationsContainerProps {
  allCatalogList?: Catalog[];
  favoriteList?: string[];
  onFavoriteUpdate?: (slug: string, remove: boolean) => void;
  setMessageAlert?: (message: string) => void;
  setSeverityAlert?: (severity: "success" | "error") => void;
  setOpenAlert?: (open: boolean) => void;
}

export const ProfileRecommendationsContainer: FC<ProfileRecommendationsContainerProps> =
  ({
    allCatalogList,
    favoriteList,
    onFavoriteUpdate,
    setMessageAlert,
    setSeverityAlert,
    setOpenAlert,
  }) => {
    const { userInfo } = useWebgate();

    let history = useHistory();
    const [recommendedServices, setRecommendedServices] = useState<
      RelevantServicesProps["services"] | undefined
    >(undefined);

    const normalizeProfileKey = (value?: string) => {
      return (value || "")
        .trim()
        .toLowerCase()
        .replace(/_/g, "-")
        .replace(/\s+/g, "-");
    };

    const resolveProfileIcon = useCallback(
      (perfilOrigem?: string): IconPerfil => {
        const key = normalizeProfileKey(perfilOrigem);
        const item = perfis.find((p) => normalizeProfileKey(p.perfil) === key);
        const icon = item?.icone ?? "icon-cidadao";
        return icon;
      },
      []
    );

    const slugToTitle = (slug: string) => {
      return slug
        .split("-")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    };

    const handleFavoriteClick = useCallback(
      (slug: string, isFavorite: boolean) => {
        onFavoriteUpdate?.(slug, isFavorite);
      },
      [onFavoriteUpdate]
    );

    const getProfileRecommendations = useCallback(async () => {
      try {
        const data = await getProfileRecommendationsByUsername(
          userInfo?.preferred_username
        );
        const services = data?.servicos_recomendados ?? [];

        const mappedServices: RelevantServicesProps["services"] = services.map(
          (service) => {
            const match = allCatalogList?.find(
              (catalog) => catalog.slug === service.slug
            );

            const catalog: Catalog | undefined = match
              ? match
              : {
                  type: "CATALOG",
                  title: service.titulo || service.slug,
                  slug: service.slug,
                  description: service.descricao || service.motivo || "",
                  tags: [],
                  isFree: true,
                  isOnline: true,
                  isDigital: true,
                  category: service.categoria || "",
                  targets: [],
                  department: "",
                  departmentSections: [],
                  categorySlug: (service.categoria || "")
                    .toLowerCase()
                    .replace(/\s+/g, "-"),
                  highlighted: false,
                  score: 0,
                  imageUrl: "",
                  icon: service.icone || "",
                  link: service.link || "",
                  resultTotal: 0,
                  accessProfile: undefined,
                  isSelfService: true,
                  catalogDataRow: [],
                };

            const isFavorite = favoriteList?.some(
              (favorite) => favorite === catalog.slug
            );

            const profileIcon = resolveProfileIcon(service.perfil_origem);

            return {
              title:
                match?.title || service.titulo || slugToTitle(service.slug),
              description:
                match?.description ||
                service.descricao ||
                service.motivo ||
                catalog.description,
              categoryIcon: catalog.icon || "fa-plus",
              profileIcon,
              onClick: () => {
                if (match?.categorySlug) {
                  history.push(`${match.categorySlug}/${match.slug}`);
                  return;
                }
                if (catalog.categorySlug) {
                  history.push(`${catalog.categorySlug}/${catalog.slug}`);
                  return;
                }
                if (catalog.link?.startsWith("http")) {
                  window.location.href = catalog.link;
                  return;
                }
                if (catalog.link?.startsWith("/")) {
                  history.push(catalog.link);
                }
              },
              imageUrl: catalog.imageUrl,
              onClickFavourite: () => {},
              type: catalog.isOnline ? "online" : "in-person",
              targets: catalog?.targets,
              accessProfile: catalog?.accessProfile,
              slug: catalog.slug,
              setSeverityAlert,
              setOpenAlert,
              setMessageAlert,
              onClickFavorite: () =>
                handleFavoriteClick(catalog.slug, isFavorite ?? false),
              isFavorite,
            };
          }
        );

        const filteredServices = mappedServices.filter((service) => {
          const match = allCatalogList?.find(
            (catalog) => catalog.slug === service.slug
          );
          const catalog =
            match || services.find((s) => s.slug === service.slug);

          if (match?.categorySlug && match?.slug) {
            return true;
          }

          const link = match?.link || catalog?.link;
          if (link && link.trim() !== "") {
            return true;
          }

          return false;
        });

        setRecommendedServices(filteredServices);
      } catch (err) {
        console.log(err);
        setRecommendedServices([]);
      }
    }, [
      allCatalogList,
      favoriteList,
      handleFavoriteClick,
      history,
      resolveProfileIcon,
      setMessageAlert,
      setOpenAlert,
      setSeverityAlert,
      userInfo?.preferred_username,
    ]);

    useEffect(() => {
      if (!userInfo?.preferred_username) {
        return;
      }
      if (recommendedServices === undefined) {
        void getProfileRecommendations();
      }
    }, [
      getProfileRecommendations,
      recommendedServices,
      userInfo?.preferred_username,
    ]);

    if (recommendedServices === undefined || recommendedServices.length === 0) {
      return null;
    }

    const relevantServicesProps: RelevantServicesProps = {
      services: recommendedServices,
    };

    return <ProfileRecommendations {...relevantServicesProps} />;
  };
